import { GetStaticPropsContext, NextPage } from 'next'
import React from 'react'
import {
  Box,
  Text,
  Flex,
  Button,
  Image,
  UnorderedList,
  ListItem,
  Link,
  Highlight,
} from '@chakra-ui/react'
import { useTranslations } from 'next-intl'

import SeoHead from '@/components/seo-head'
import { START_INDIVIDUAL_KYC, SUPPORT_MARKETPLACE_URL } from '@/constants'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { MobileIcon } from '@/components/icons/MobileIcon'
import { ValidIdIcon } from '@/components/icons/ValidIdIcon'
import { CameraIcon } from '@/components/icons/CameraIcon'
import { HomeIcon } from '@/components/icons/HomeIcon'
import { TextSnippetIcon } from '@/components/icons/TextSnippetIcon'

const Icons = [
  (props: any) => <MobileIcon {...props} />,
  (props: any) => <ValidIdIcon {...props} />,
  (props: any) => <CameraIcon {...props} />,
  (props: any) => <TextSnippetIcon {...props} />,
]

const FaqItems = () => {
  const t = useTranslations()
  const items = [
    {
      title: t('marketplace-kyc.faq.subtitle1'),
      descriptions: [t('marketplace-kyc.faq.description1')],
      videoUrl: 'https://vimeo.com/831433902',
    },
    {
      title: t('marketplace-kyc.faq.subtitle2'),
      descriptions: [
        t('marketplace-kyc.faq.description21'),
        t('marketplace-kyc.faq.description22'),
      ],
    },
    {
      title: t('marketplace-kyc.faq.subtitle3'),
      descriptions: [
        t('marketplace-kyc.faq.description31'),
        t('marketplace-kyc.faq.description32'),
        t('marketplace-kyc.faq.description33'),
      ],
    },
    {
      title: t('marketplace-kyc.faq.subtitle4'),
      descriptions: [
        t('marketplace-kyc.faq.description41'),
        t('marketplace-kyc.faq.description42'),
      ],
    },
    {
      title: t('marketplace-kyc.faq.subtitle5'),
      descriptions: [
        t('marketplace-kyc.faq.description51'),
        t('marketplace-kyc.faq.description52'),
      ],
    },
    {
      title: t('marketplace-kyc.faq.subtitle6'),
      descriptions: [
        t('marketplace-kyc.faq.description61'),
        t('marketplace-kyc.faq.description62'),
      ],
    },
    {
      title: t('marketplace-kyc.faq.subtitle7'),
      descriptions: [
        t('marketplace-kyc.faq.description71'),
        t('marketplace-kyc.faq.description72'),
        t('marketplace-kyc.faq.description73'),
      ],
    },
    {
      title: t('marketplace-kyc.faq.subtitle8'),
      descriptions: [
        t('marketplace-kyc.faq.description81'),
        t('marketplace-kyc.faq.description82'),
      ],
    },
  ]
  return (
    <Flex flexDirection="column" gap="24px">
      {items.map((item, index) => (
        <Box key={`faq-${index}`}>
          <Text fontSize="20px" fontWeight={600} fontFamily="Inter">
            {item.title}
          </Text>
          {item.videoUrl && (
            <Box py="8px">
              <iframe
                src="https://player.vimeo.com/video/831433902?h=35593bcb82&byline=0&portrait=0"
                width="277"
                height="596"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen={true}
              />
            </Box>
          )}
          <UnorderedList pl={3}>
            {item.descriptions.map((description, descriptionIndex) => (
              <ListItem key={`faq-${index}-${descriptionIndex}`}>
                <Text fontSize="16px" fontWeight={500} fontFamily="Inter">
                  {description}
                </Text>
              </ListItem>
            ))}
          </UnorderedList>
        </Box>
      ))}
    </Flex>
  )
}

const GeneralVerificationPage: NextPage = () => {
  const t = useTranslations()

  return (
    <>
      <SeoHead
        title={t('marketplace-kyc.seo.title')}
        breadcrumbTitle={t('marketplace-kyc.seo.breadcrumb')}
        description={t('marketplace-kyc.seo.description')}
      />
      <Flex justifyContent="center" flexDirection="column" alignItems="center">
        <ContainerBase px={{ base: 0, lg: 4 }}>
          <Flex
            justifyContent={{ base: 'center', lg: 'space-between' }}
            alignItems={{ base: 'center', lg: 'flex-start' }}
            flexDirection={{ base: 'column', lg: 'row' }}
            pt="49px"
            pb="54px"
          >
            <Flex
              flexDirection="column"
              px={{ base: '39px', md: 0 }}
              maxWidth={{ base: 'auto', lg: '469px' }}
              gap="16px"
              pb={{ base: '53px', lg: '0' }}
            >
              <Text fontSize="30px" fontFamily="Inter" fontWeight="700" as="h1">
                {t('marketplace-kyc.title')}
              </Text>
              <Text
                fontSize="16px"
                fontFamily="Inter"
                _dark={{
                  color: 'white',
                }}
              >
                <Highlight
                  query={t('marketplace-kyc.description2')}
                  styles={{
                    fontWeight: '700',
                    whiteSpace: 'normal',
                    color: 'inherit',
                  }}
                >
                  {t('marketplace-kyc.description')}
                </Highlight>
              </Text>
              <Link href={START_INDIVIDUAL_KYC} isExternal>
                <Button
                  bg="brand.600"
                  color="white"
                  _hover={{
                    bg: 'black',
                    opacity: 0.7,
                  }}
                  width="264px"
                  height={{ base: '40px', xl: '50px' }}
                  fontWeight={400}
                  fontFamily="Inter"
                >
                  {t('marketplace-kyc.start-individual')}
                </Button>
              </Link>

              <Link href={SUPPORT_MARKETPLACE_URL} isExternal>
                <Text
                  as="u"
                  fontSize="16px"
                  color="brand.600"
                  fontFamily="Inter"
                >
                  {t('marketplace-kyc.start-entities')}
                </Text>
              </Link>
            </Flex>
            <Image
              src="/img/kyc-verification-phone.jpg"
              height="100%"
              width="100%"
              objectFit="contain"
              alt="book"
              maxWidth="517px"
            />
          </Flex>
        </ContainerBase>
        <Box bg="gray.50" _dark={{ bg: 'gray.950' }} width="100%">
          <ContainerBase px={{ base: 0, lg: 4 }} pt="33px" pb="48px">
            <Flex flexDirection="column">
              <Text
                fontSize="24px"
                fontWeight={700}
                fontFamily="Inter"
                align="center"
              >
                {t('marketplace-kyc.process.title')}
              </Text>
              <Flex
                flexDirection="column"
                gap={4}
                pt="28px"
                px={{ base: '39px', md: 0 }}
              >
                {Array(4)
                  .fill(0)
                  .map((x, index) => (
                    <Box
                      key={`process-${index}`}
                      width="100%"
                      p="16px"
                      bg="white"
                      _dark={{ bg: 'black' }}
                      borderRadius="8px"
                      border={1}
                      borderColor="gray.220"
                    >
                      <Flex gap={4}>
                        {Icons[index]({
                          width: '24px',
                          height: '24px',
                          color: '#3C53C9',
                        })}
                        <Box>
                          <Text
                            fontSize="16px"
                            fontWeight="600"
                            fontFamily="Inter"
                          >
                            {t(`marketplace-kyc.process.items.${index}.title`)}
                          </Text>

                          <Text
                            fontSize="16px"
                            fontWeight="400"
                            fontFamily="Inter"
                          >
                            {t(
                              `marketplace-kyc.process.items.${index}.description`
                            )}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  ))}
              </Flex>
            </Flex>
          </ContainerBase>
        </Box>
        <ContainerBase px={{ base: 0, lg: 4 }} pt="33px">
          <Box px={{ base: '39px', md: 0 }}>
            <Text fontSize="48px" fontWeight={700} fontFamily="Inter" pb="24px">
              {t('marketplace-kyc.faq.title')}
            </Text>
            <FaqItems />
          </Box>
        </ContainerBase>
      </Flex>
    </>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
    },
  }
}

export default GeneralVerificationPage
